import { useSystemClassifierItemById } from '../../configuration';
import { DataObjectGroupType, DataObjectView } from '../types';
import { Descriptions, Divider } from 'antd';
import styled from 'styled-components';
import { useMemo } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from '../translations/CalendarView.translations.json';
import { ExecutionFormDataType } from '../schema';
import { DescriptionsItemType } from 'antd/es/descriptions';
import { DetailsCalendarTable } from './DetailsCalendarTable';
import { renderLoadingType } from './utils';
import { ExecutionPeriodsView } from './ExecutionPeriods';
import { ExecutionMethod } from './types';
import { Padding } from '../../common/components';
import { ExecutionPublicationsViewTable } from './ExecutionPublicationsViewTable';

const { addTranslations, useTranslation } = i18nNsFactory(
  'execution-calendar-view',
);
addTranslations(translations);

export interface CalendarViewProps {
  dataObjectView: DataObjectView;
  execution: ExecutionFormDataType | null;
}

export function CalendarView({ dataObjectView, execution }: CalendarViewProps) {
  const { t } = useTranslation();
  const method = useSystemClassifierItemById(
    'EXECUTION_METHOD',
    execution?.executionMethodId ?? undefined,
  );
  const loadingType = useSystemClassifierItemById(
    'PERIOD_LOADING_TYPE',
    execution?.periodLoadingTypeId ?? undefined,
  )?.code;
  const sourceDataVersion = useSystemClassifierItemById(
    'SOURCE_DATA_VERSION',
    execution?.sourceDataVersionId ?? undefined,
  );
  const isInput = dataObjectView.dataObjectGroupType === DataObjectGroupType.I;
  const isOutput = dataObjectView.dataObjectGroupType === DataObjectGroupType.O;

  const autoLoadingPaused = useMemo(() => {
    if (!isInput) {
      return [];
    }

    return [
      {
        key: 'autoLoadingPaused',
        label: t('column.autoLoadingPaused.label'),
        children: execution?.autoLoadingPaused
          ? t('column.autoLoadingPaused.yes')
          : t('column.autoLoadingPaused.no'),
      },
    ];
  }, [execution, isInput, t]);

  const sampleItems = useMemo(() => {
    if (!isInput) {
      return [];
    }

    const loadSample = {
      key: 'loadSample',
      label: t('column.loadSample.label'),
      children: execution?.loadSample
        ? t('column.loadSample.yes')
        : t('column.loadSample.no'),
    };

    if (execution?.loadSample) {
      return [
        loadSample,
        {
          key: 'sampleLoadingDays',
          label: t('column.sampleLoadingDays.label'),
          children: execution?.loadSample
            ? execution?.sampleLoadingDays +
              t('column.sampleLoadingDays.suffix')
            : null,
        },
        {
          key: 'sampleLoadingTime',
          label: t('column.sampleLoadingTime.label'),
          children: execution?.sampleLoadingTime,
        },
      ];
    }

    return [loadSample];
  }, [execution, isInput, t]);

  const methodItems = useMemo(() => {
    const executionMethod: DescriptionsItemType = {
      key: 'executionMethodId',
      label: isInput
        ? t('column.executionMethodId.in')
        : t('column.executionMethodId.out'),
      children: method?.name,
    };

    if (method?.code === ExecutionMethod.MANUAL) {
      return [executionMethod];
    }

    const columns = [
      executionMethod,
      {
        key: 'periodLoadingTypeId',
        label: isInput
          ? t('column.periodLoadingTypeId.in')
          : t('column.periodLoadingTypeId.out'),
        children:
          loadingType === 'FIXED'
            ? renderLoadingType(loadingType, execution?.periodCount)
            : loadingType
              ? t(`column.periodLoadingTypeId.${loadingType}`)
              : '',
      },
    ];

    if (method?.code === ExecutionMethod.CALENDAR) {
      columns.push({
        key: 'executionTime',
        label: isInput
          ? t('column.executionTime.in')
          : t('column.executionTime.out'),
        children: execution?.executionTime,
      });
    }

    if (method?.code === ExecutionMethod.PERIODIC) {
      columns.push({
        key: 'periodSkipCount',
        label: t('column.periodSkipCount.label'),
        children: execution?.periodSkipCount,
      });

      if (isInput) {
        columns.push({
          key: 'logLoadingSuccess',
          label: t('column.logLoadingSuccess.label'),
          children: execution?.logLoadingSuccess
            ? t('column.logLoadingSuccess.yes')
            : t('column.logLoadingSuccess.no'),
        });
      }
    }

    if (isOutput) {
      columns.push({
        key: 'sourceDataVersionId',
        label: t('column.sourceDataVersionId.label'),
        children: sourceDataVersion?.name,
      });
    }

    return columns;
  }, [
    execution?.executionTime,
    execution?.logLoadingSuccess,
    execution?.periodCount,
    execution?.periodSkipCount,
    isInput,
    isOutput,
    loadingType,
    method?.code,
    method?.name,
    sourceDataVersion,
    t,
  ]);

  return (
    <ContentWrapper>
      {isInput && (
        <>
          <SectionDescriptions
            colon={false}
            items={autoLoadingPaused}
            layout="vertical"
          />
          <SectionDivider orientation="left">
            {t('section.sample')}
          </SectionDivider>
          <SectionDescriptions
            colon={false}
            items={sampleItems}
            layout="vertical"
          />
        </>
      )}
      <SectionDivider orientation="left">{t('section.method')}</SectionDivider>
      <SectionDescriptions
        colon={false}
        items={methodItems}
        layout="vertical"
      />
      <Padding vertical>
        {method?.code === ExecutionMethod.CALENDAR && (
          <DetailsCalendarTable
            groupType={dataObjectView.dataObjectGroupType}
            calendar={execution?.executionCalendar ?? []}
            loadingType={loadingType}
          />
        )}
        {method?.code === ExecutionMethod.PERIODIC && (
          <ExecutionPeriodsView
            groupType={dataObjectView.dataObjectGroupType}
            execution={execution}
            loadingType={loadingType}
          />
        )}
      </Padding>
      {isOutput && (
        <>
          <SectionDivider orientation="left">
            {t('section.publish')}
          </SectionDivider>
          <SectionDescriptions
            items={[
              {
                key: 'publicationTime',
                label: t('column.publicationTime.label'),
                children: execution?.publicationTime,
              },
            ]}
            layout="vertical"
          />
          <Padding vertical>
            <ExecutionPublicationsViewTable
              publications={execution?.executionPublications ?? []}
            />
          </Padding>
        </>
      )}
    </ContentWrapper>
  );
}

const ContentWrapper = styled.div`
  width: 100%;
`;

const SectionDivider = styled(Divider)`
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const SectionDescriptions = styled(Descriptions)`
  margin-top: 24px;
`;
